import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';

import NoHoverButton from '../../../shared/components/NoHoverButton';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import PickerModal, { PickerModalSearch } from '../../../shared/_TBComponents/PickerModal';

import { setActiveReduxModal, setSelectedGroups } from '../ducks/feedActions';
import { alphabetizeGroups } from '../helperFunctions';
import { feedTracker } from '../../../shared/utils/amplitudeHelper';

const ActionWrapper = styled('div')`
  align-items: center;
  background: #ffffff;
  border-bottom-left-radius: 50%;
  border-bottom-right-radius: 50%;
  border-top: 1px solid #f0f0f0;
  bottom: 0;
  color: #999;
  display: flex;
  font-size: 12px;
  font-weight: 500;
  height: 50px;
  justify-content: end;
  width: 100%;
  z-index: 1;
  .submit-button {
    appearance: none;
    align-items: center;
    background: #0dcc8a;
    border: 0;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    color: #fff;
    display: flex;
    font-size: 14px;
    font-weight: 900;
    height: 100%;
    justify-content: center;
    outline: none !important;
    padding: 6.5px 25px;
    text-decoration: none;
    width: 100%;
  }
`;

const GroupItem = styled('div')`
  align-items: center;
  display: flex;
  font-size: 16px;
  font-weight: 500;
  height: 50px;
  justify-content: space-between;
  margin-bottom: 4px;
  padding: 8px;
  top: 0;
  width: calc(100% + 14px);
  z-index: 1;
  ${(props) => props.selected
    && `
    background-color: #CFF5E8;
    border-radius: 8px;
  `}
  &:hover {
    background-color: #eeeeee;
    border-radius: 8px;
    font-weight: 500;
  }
`;

const GroupItemName = styled('span')`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GroupOptionsDiv = styled('div')`
  background-color: #ffffff;
  color: #444444;
  margin: 0 auto;
  text-align: center;
  width: 90%;
`;

const GroupsContainer = styled('div')`
  height: 400px;
  margin: 8px auto 0 auto;
  overflow: auto;
  scrollbar-gutter: stable;
  width: 100%;
`;

const FilterGroupsModal = ({ onRequestClose }) => {
  const groups = useSelector((state) => state.feed.data.groups);
  const selectedGroups = useSelector((state) => state.feed.data.selectedGroups);

  const [localSelectedGroups, setLocalSelectedGroups] = useState(selectedGroups);
  const [searchText, setSearchText] = useState('');

  const dispatch = useDispatch();

  const searchedGroups = useMemo(() => (groupsArray) => groupsArray.filter(
    (group) => group.name.toLowerCase().includes(searchText.trim().toLowerCase()),
  ), [searchText]);

  const unselectedGroups = useMemo(() => groups.length > 0
    && groups.filter(
      (group) => !localSelectedGroups.some((localGroup) => localGroup.id === group.id),
    ), [groups, localSelectedGroups]);

  const numSelectedGroups = localSelectedGroups.length;
  const buttonText = numSelectedGroups > 0
    ? `${numSelectedGroups} ${numSelectedGroups === 1 ? 'GROUP' : 'GROUPS'}`
    : '';

  const handleCloseModal = () => {
    setSearchText('');
    onRequestClose();
  };

  const handleChangeSearch = (e) => {
    e.preventDefault();
    setSearchText(e.target.value);
  };

  const handleToggleSelected = (group) => {
    let updatedGroups = [];
    if (!localSelectedGroups.includes(group)) {
      updatedGroups = alphabetizeGroups([...localSelectedGroups, group]);
    } else {
      updatedGroups = alphabetizeGroups(localSelectedGroups.filter(
        (currentGroup) => currentGroup.id !== group.id,
      ));
    }
    setLocalSelectedGroups(updatedGroups);
  };

  const handleChangeSelectedGroups = () => {
    batch(() => {
      dispatch(setSelectedGroups(localSelectedGroups));
      dispatch(setActiveReduxModal(''));
    });
    feedTracker('TV Mode', 'Group filter submitted');
  };

  const renderSelectedGroups = () => (
    localSelectedGroups.length > 0 && (
      <GroupOptionsDiv>
        {searchedGroups(localSelectedGroups).map((group) => (
          <GroupItem
            key={group.id}
            onClick={() => handleToggleSelected(group)}
            selected
          >
            <GroupItemName>{group.name}</GroupItemName>
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='checkmark'
              color='#0DCC8A'
            />
          </GroupItem>
        ))}
      </GroupOptionsDiv>
    )
  );

  const renderUnselectedGroups = () => (
    <GroupOptionsDiv>
      {unselectedGroups.length > 0
        && searchedGroups(unselectedGroups).map((group) => (
          <GroupItem
            key={group.id}
            onClick={() => handleToggleSelected(group)}
          >
            <GroupItemName>{group.name}</GroupItemName>
          </GroupItem>
        ))}
    </GroupOptionsDiv>
  );

  return (
    <PickerModal
      customPadding='0px'
      onRequestClose={handleCloseModal}
      headerText='Filter by Groups'
      headerIcon='group'
    >
      <PickerModalSearch
        placeholder='Search for a group'
        value={searchText}
        onChange={handleChangeSearch}
      />
      <GroupsContainer>
        {renderSelectedGroups()}
        {renderUnselectedGroups()}
      </GroupsContainer>
      <ActionWrapper>
        <NoHoverButton
          label='Submit'
          cta={`SAVE ${buttonText}`}
          type='button'
          className='submit-button'
          onClick={handleChangeSelectedGroups}
          color='#0DCC8A'
        >
          Confirm
        </NoHoverButton>
      </ActionWrapper>
    </PickerModal>
  );
};

FilterGroupsModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
};

export default FilterGroupsModal;
